import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Toaster, toast } from 'sonner';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Star, MapPin, Plus, Edit, Trash2, LogOut, User, Heart, Baby, Menu, X, Home, BookOpen, Map, Download, Copy } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Fix Leaflet default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Map Click Handler Component
const MapClickHandler = ({ onMapClick }) => {
  useMapEvents({
    click: (e) => {
      onMapClick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
};

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (email, password) => {
    const response = await axios.post(`${API}/auth/register`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFF8F5]">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <Baby className="w-12 h-12 text-[#E8A598]" />
          <p className="text-[#6B5B5B]">Cargando...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Star Rating Component
const StarRating = ({ value, onChange, readonly = false, size = "md" }) => {
  const sizes = { sm: "w-4 h-4", md: "w-5 h-5", lg: "w-6 h-6" };
  
  const handleStarClick = (star) => {
    if (readonly || !onChange) return;
    // If clicking the same star that's already selected, reset to 0
    if (star === value) {
      onChange(0);
    } else {
      onChange(star);
    }
  };
  
  return (
    <div className="flex gap-1 items-center">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={readonly}
          onClick={() => handleStarClick(star)}
          className={`${readonly ? 'cursor-default' : 'cursor-pointer hover:scale-110'} transition-transform`}
        >
          <Star
            className={`${sizes[size]} ${star <= value ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`}
          />
        </button>
      ))}
      {!readonly && value > 0 && (
        <button
          type="button"
          onClick={() => onChange(0)}
          className="ml-2 text-xs text-[#8B7B7B] hover:text-[#E8A598] underline"
          title="Quitar valoración"
        >
          ✕
        </button>
      )}
    </div>
  );
};

// Navigation Component
const Navigation = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Sesión cerrada');
  };

  return (
    <nav className="bg-white/80 backdrop-blur-md border-b border-[#F5E6E0] sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <Link to="/" className="flex items-center gap-2">
              <Heart className="w-8 h-8 text-[#E8A598]" />
              <span className="font-['Playfair_Display'] text-xl font-semibold text-[#5D4E4E]">Mi Historia de Parto</span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <Link to="/stories" className="text-[#6B5B5B] hover:text-[#E8A598] transition-colors flex items-center gap-2">
              <BookOpen className="w-4 h-4" />
              Historias
            </Link>
            <Link to="/map" className="text-[#6B5B5B] hover:text-[#E8A598] transition-colors flex items-center gap-2">
              <Map className="w-4 h-4" />
              Mapa
            </Link>
            {user ? (
              <>
                <Link to="/my-stories" className="text-[#6B5B5B] hover:text-[#E8A598] transition-colors flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Mis Historias
                </Link>
                {user.is_admin && (
                  <Link to="/instagram-manager" className="text-[#6B5B5B] hover:text-[#E8A598] transition-colors flex items-center gap-2">
                    📱 Instagram
                  </Link>
                )}
                <Button 
                  onClick={handleLogout}
                  variant="ghost"
                  className="text-[#6B5B5B] hover:text-[#E8A598] hover:bg-[#FFF0EB]"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Salir
                </Button>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" className="text-[#6B5B5B] hover:text-[#E8A598]" data-testid="login-nav-btn">
                    Iniciar Sesión
                  </Button>
                </Link>
                <Link to="/register">
                  <Button className="bg-[#E8A598] hover:bg-[#D99285] text-white" data-testid="register-nav-btn">
                    Registrarse
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-[#6B5B5B] hover:text-[#E8A598] p-2"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-[#F5E6E0]">
            <div className="flex flex-col gap-2">
              <Link 
                to="/stories" 
                className="px-4 py-2 text-[#6B5B5B] hover:bg-[#FFF0EB] rounded-lg"
                onClick={() => setMobileMenuOpen(false)}
              >
                Historias
              </Link>
              <Link 
                to="/map" 
                className="px-4 py-2 text-[#6B5B5B] hover:bg-[#FFF0EB] rounded-lg"
                onClick={() => setMobileMenuOpen(false)}
              >
                Mapa
              </Link>
              {user ? (
                <>
                  <Link 
                    to="/my-stories" 
                    className="px-4 py-2 text-[#6B5B5B] hover:bg-[#FFF0EB] rounded-lg"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Mis Historias
                  </Link>
                  {user.is_admin && (
                    <Link 
                      to="/instagram-manager" 
                      className="px-4 py-2 text-[#6B5B5B] hover:bg-[#FFF0EB] rounded-lg"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      📱 Instagram
                    </Link>
                  )}
                  <button 
                    onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                    className="px-4 py-2 text-left text-[#6B5B5B] hover:bg-[#FFF0EB] rounded-lg"
                  >
                    Cerrar Sesión
                  </button>
                </>
              ) : (
                <>
                  <Link 
                    to="/login" 
                    className="px-4 py-2 text-[#6B5B5B] hover:bg-[#FFF0EB] rounded-lg"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Iniciar Sesión
                  </Link>
                  <Link 
                    to="/register" 
                    className="px-4 py-2 text-[#6B5B5B] hover:bg-[#FFF0EB] rounded-lg"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Registrarse
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Landing Page
const LandingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#FFF8F5]">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#FFF0EB] to-transparent" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32 relative">
          <div className="text-center max-w-3xl mx-auto">
            <div className="flex justify-center mb-6">
              <Baby className="w-16 h-16 text-[#E8A598]" />
            </div>
            <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl font-bold text-[#5D4E4E] mb-6">
              Tu historia de parto importa
            </h1>
            <p className="text-lg text-[#6B5B5B] mb-8 leading-relaxed">
              Un espacio seguro y anónimo donde las mujeres pueden compartir sus experiencias de parto, 
              ayudando a otras madres a tomar decisiones informadas sobre dónde dar a luz.
            </p>
            <div className="flex flex-col gap-6 items-center max-w-2xl mx-auto">
              {user ? (
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button 
                    onClick={() => navigate('/stories/new')}
                    className="bg-[#E8A598] hover:bg-[#D99285] text-white px-8 py-6 text-lg"
                    data-testid="share-story-btn"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    Compartir mi historia
                  </Button>
                  <Button 
                    onClick={() => navigate('/stories')}
                    variant="outline"
                    className="border-[#E8A598] text-[#E8A598] hover:bg-[#FFF0EB] px-8 py-6 text-lg"
                    data-testid="read-stories-btn"
                  >
                    Leer historias
                  </Button>
                </div>
              ) : (
                <>
                  {/* Main CTA - Leer Historias */}
                  <Button 
                    onClick={() => navigate('/stories')}
                    className="bg-[#E8A598] hover:bg-[#D99285] text-white px-12 py-7 text-xl font-semibold shadow-lg hover:shadow-xl transition-all w-full sm:w-auto group"
                    data-testid="browse-without-register-btn"
                  >
                    <BookOpen className="w-6 h-6 mr-3 group-hover:scale-110 transition-transform" />
                    Leer las historias
                  </Button>
                  
                  {/* Divider */}
                  <div className="flex items-center gap-4 w-full max-w-md">
                    <div className="flex-1 border-t border-[#E8D8D8]"></div>
                    <span className="text-sm text-[#8B7B7B]">o</span>
                    <div className="flex-1 border-t border-[#E8D8D8]"></div>
                  </div>
                  
                  {/* Secondary CTAs - Registro */}
                  <div className="flex flex-col gap-3 w-full">
                    <p className="text-center text-[#6B5B5B] text-sm mb-2">
                      ¿Quieres compartir tu experiencia?
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      <Button 
                        onClick={() => navigate('/register')}
                        variant="outline"
                        className="border-[#E8A598] text-[#E8A598] hover:bg-[#FFF0EB] px-6 py-3"
                        data-testid="get-started-btn"
                      >
                        Crear cuenta
                      </Button>
                      <Button 
                        onClick={() => navigate('/login')}
                        variant="ghost"
                        className="text-[#6B5B5B] hover:text-[#E8A598] hover:bg-[#FFF0EB] px-6 py-3"
                        data-testid="login-btn"
                      >
                        Iniciar sesión
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-['Playfair_Display'] text-3xl font-semibold text-center text-[#5D4E4E] mb-12">
            ¿Por qué compartir tu experiencia?
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-none shadow-sm bg-[#FFF8F5]">
              <CardHeader>
                <div className="w-12 h-12 bg-[#FFF0EB] rounded-full flex items-center justify-center mb-4">
                  <Heart className="w-6 h-6 text-[#E8A598]" />
                </div>
                <CardTitle className="text-[#5D4E4E] font-['Playfair_Display']">Anonimato garantizado</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-[#6B5B5B]">
                  Tu identidad permanece completamente anónima. Solo tú decides qué compartir de tu experiencia.
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-sm bg-[#FFF8F5]">
              <CardHeader>
                <div className="w-12 h-12 bg-[#FFF0EB] rounded-full flex items-center justify-center mb-4">
                  <MapPin className="w-6 h-6 text-[#E8A598]" />
                </div>
                <CardTitle className="text-[#5D4E4E] font-['Playfair_Display']">Mapa de hospitales</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-[#6B5B5B]">
                  Explora experiencias en hospitales de toda España con nuestro mapa interactivo y valoraciones reales.
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-sm bg-[#FFF8F5]">
              <CardHeader>
                <div className="w-12 h-12 bg-[#FFF0EB] rounded-full flex items-center justify-center mb-4">
                  <Star className="w-6 h-6 text-[#E8A598]" />
                </div>
                <CardTitle className="text-[#5D4E4E] font-['Playfair_Display']">Valoraciones detalladas</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-[#6B5B5B]">
                  Valora por separado el hospital, el servicio de ginecología y las matronas para dar una imagen completa.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* About Section - Julia */}
      <section className="py-20 bg-[#FFF8F5]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="aspect-square rounded-2xl overflow-hidden shadow-xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_maternity-tales-1/artifacts/o48bjl4o_WhatsApp%20Image%202025-11-29%20at%2022.07.33%20%281%29.jpeg"
                  alt="Julia - Madre y Matrona"
                  className="w-full h-full object-cover brightness-110"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 bg-[#E8A598] text-white px-4 py-2 rounded-lg shadow-lg">
                <p className="font-['Playfair_Display'] text-sm">Madre & Matrona</p>
              </div>
            </div>
            <div className="space-y-6">
              <h2 className="font-['Playfair_Display'] text-3xl font-semibold text-[#5D4E4E]">
                Quién está detrás
              </h2>
              <div className="space-y-4">
                <p className="text-lg text-[#6B5B5B] leading-relaxed">
                  Me llamo <span className="font-semibold text-[#5D4E4E]">Julia</span>, soy madre y matrona.
                </p>
                <p className="text-[#6B5B5B] leading-relaxed">
                  He creado esta plataforma para ti, querida madre. Para visibilizar los partos, para que puedas compartir tu historia y ser fuente de inspiración para otras.
                </p>
                <p className="text-[#6B5B5B] leading-relaxed">
                  Juntas construimos un mundo mejor.
                </p>
                <p className="text-[#E8A598] italic font-medium">
                  "Gracias por compartir este recuerdo tan preciado."
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#5D4E4E] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-center mb-4">
            <Heart className="w-8 h-8 text-[#E8A598]" />
          </div>
          <p className="font-['Playfair_Display'] text-lg mb-2">Mi Historia de Parto</p>
          <p className="text-[#B8A8A8] text-sm">
            Un espacio creado por y para mujeres
          </p>
        </div>
      </footer>
    </div>
  );
};

// Login Page
const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('¡Bienvenida de nuevo!');
      navigate('/stories');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFF8F5]">
      <Navigation />
      <div className="flex items-center justify-center py-12 px-4">
        <Card className="w-full max-w-md border-none shadow-lg">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <Heart className="w-12 h-12 text-[#E8A598]" />
            </div>
            <CardTitle className="font-['Playfair_Display'] text-2xl text-[#5D4E4E]">Iniciar Sesión</CardTitle>
            <CardDescription className="text-[#6B5B5B]">Accede a tu cuenta para compartir tu historia</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#5D4E4E]">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="border-[#E8D8D8] focus:border-[#E8A598] focus:ring-[#E8A598]"
                  placeholder="tu@email.com"
                  data-testid="login-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#5D4E4E]">Contraseña</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="border-[#E8D8D8] focus:border-[#E8A598] focus:ring-[#E8A598]"
                  placeholder="••••••••"
                  data-testid="login-password-input"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full bg-[#E8A598] hover:bg-[#D99285] text-white"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? 'Entrando...' : 'Entrar'}
              </Button>
            </form>
            <p className="text-center mt-4 text-[#6B5B5B]">
              ¿No tienes cuenta?{' '}
              <Link to="/register" className="text-[#E8A598] hover:underline">Regístrate</Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Register Page
const RegisterPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    if (password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    setLoading(true);
    try {
      await register(email, password);
      toast.success('¡Cuenta creada con éxito!');
      navigate('/stories');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrarse');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFF8F5]">
      <Navigation />
      <div className="flex items-center justify-center py-12 px-4">
        <Card className="w-full max-w-md border-none shadow-lg">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <Baby className="w-12 h-12 text-[#E8A598]" />
            </div>
            <CardTitle className="font-['Playfair_Display'] text-2xl text-[#5D4E4E]">Crear Cuenta</CardTitle>
            <CardDescription className="text-[#6B5B5B]">Únete a nuestra comunidad de madres</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#5D4E4E]">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="border-[#E8D8D8] focus:border-[#E8A598] focus:ring-[#E8A598]"
                  placeholder="tu@email.com"
                  data-testid="register-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#5D4E4E]">Contraseña</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="border-[#E8D8D8] focus:border-[#E8A598] focus:ring-[#E8A598]"
                  placeholder="Mínimo 6 caracteres"
                  data-testid="register-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-[#5D4E4E]">Confirmar Contraseña</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="border-[#E8D8D8] focus:border-[#E8A598] focus:ring-[#E8A598]"
                  placeholder="Repite tu contraseña"
                  data-testid="register-confirm-password-input"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full bg-[#E8A598] hover:bg-[#D99285] text-white"
                disabled={loading}
                data-testid="register-submit-btn"
              >
                {loading ? 'Creando cuenta...' : 'Crear cuenta'}
              </Button>
            </form>
            <p className="text-center mt-4 text-[#6B5B5B]">
              ¿Ya tienes cuenta?{' '}
              <Link to="/login" className="text-[#E8A598] hover:underline">Inicia sesión</Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Stories List Page
const StoriesPage = () => {
  const [stories, setStories] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterProvince, setFilterProvince] = useState('');
  const [filterHospital, setFilterHospital] = useState('');
  const [filterHomeBirth, setFilterHomeBirth] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    fetchData();
  }, [filterProvince, filterHospital, filterHomeBirth]);

  const fetchData = async () => {
    try {
      const [storiesRes, hospitalsRes] = await Promise.all([
        axios.get(`${API}/stories`, {
          params: {
            province: filterProvince || undefined,
            hospital_id: filterHospital || undefined,
            home_birth: filterHomeBirth || undefined
          }
        }),
        axios.get(`${API}/hospitals`)
      ]);
      setStories(storiesRes.data);
      setHospitals(hospitalsRes.data);
    } catch (error) {
      toast.error('Error al cargar las historias');
    } finally {
      setLoading(false);
    }
  };

  const provinces = [...new Set(hospitals.map(h => h.province))].sort();

  const getBirthTypeBadge = (type) => {
    const types = {
      'vaginal_normal': { label: 'Parto vaginal normal', color: 'bg-green-100 text-green-700' },
      'vaginal_instrumental': { label: 'Parto con ventosa/fórceps', color: 'bg-amber-100 text-amber-700' },
      'cesarean': { label: 'Cesárea', color: 'bg-blue-100 text-blue-700' },
      'natural': { label: 'Parto Natural', color: 'bg-green-100 text-green-700' },
      'induced': { label: 'Parto Inducido', color: 'bg-purple-100 text-purple-700' }
    };
    const t = types[type] || { label: type, color: 'bg-gray-100 text-gray-700' };
    return <Badge className={`${t.color} font-normal`}>{t.label}</Badge>;
  };

  return (
    <div className="min-h-screen bg-[#FFF8F5]">
      <Navigation />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="font-['Playfair_Display'] text-3xl font-semibold text-[#5D4E4E]">
              Historias de Parto
            </h1>
            {!user && (
              <p className="text-sm text-[#8B7B7B] mt-1">
                ✨ Regístrate para compartir tu historia
              </p>
            )}
          </div>
          <Button 
            onClick={() => user ? navigate('/stories/new') : navigate('/register')}
            className="bg-[#E8A598] hover:bg-[#D99285] text-white"
            data-testid="new-story-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Compartir mi historia
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8 flex-wrap">
          <Select value={filterProvince} onValueChange={setFilterProvince}>
            <SelectTrigger className="w-full sm:w-[200px] border-[#E8D8D8]" data-testid="filter-province-select">
              <SelectValue placeholder="Filtrar por provincia" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las provincias</SelectItem>
              {provinces.map(p => (
                <SelectItem key={p} value={p}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterHospital} onValueChange={(val) => { setFilterHospital(val); if(val) setFilterHomeBirth(false); }}>
            <SelectTrigger className="w-full sm:w-[280px] border-[#E8D8D8]" data-testid="filter-hospital-select">
              <SelectValue placeholder="Filtrar por hospital" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los hospitales</SelectItem>
              {hospitals
                .filter(h => !filterProvince || filterProvince === 'all' || h.province === filterProvince)
                .map(h => (
                  <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>
                ))}
            </SelectContent>
          </Select>
          <Button 
            variant={filterHomeBirth ? "default" : "outline"}
            onClick={() => { setFilterHomeBirth(!filterHomeBirth); if(!filterHomeBirth) setFilterHospital(''); }}
            className={filterHomeBirth 
              ? "bg-[#E8A598] hover:bg-[#D99285] text-white" 
              : "border-[#E8D8D8] text-[#6B5B5B] hover:bg-[#FFF0EB]"}
            data-testid="filter-home-birth-btn"
          >
            <Home className="w-4 h-4 mr-2" />
            Parto en domicilio
          </Button>
          {(filterProvince || filterHospital || filterHomeBirth) && (
            <Button 
              variant="ghost" 
              onClick={() => { setFilterProvince(''); setFilterHospital(''); setFilterHomeBirth(false); }}
              className="text-[#6B5B5B]"
            >
              Limpiar filtros
            </Button>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-pulse text-[#6B5B5B]">Cargando historias...</div>
          </div>
        ) : stories.length === 0 ? (
          <Card className="border-none shadow-sm bg-white">
            <CardContent className="py-12 text-center">
              <Baby className="w-12 h-12 text-[#E8A598] mx-auto mb-4" />
              <p className="text-[#6B5B5B]">No hay historias todavía. ¡Sé la primera en compartir!</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6">
            {stories.map((story) => (
              <Card key={story.id} className="border-none shadow-sm hover:shadow-md transition-shadow bg-white" data-testid={`story-card-${story.id}`}>
                <CardHeader>
                  <div className="flex flex-col sm:flex-row justify-between gap-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <CardTitle className="text-[#5D4E4E] font-['Playfair_Display'] text-xl">
                          {story.title}
                        </CardTitle>
                        {user && story.instagram_consent && (
                          <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs">
                            📱 IG
                          </Badge>
                        )}
                      </div>
                      <CardDescription className="flex items-center gap-2 mt-1">
                        <MapPin className="w-4 h-4" />
                        {story.hospital_name} • {story.hospital_province}
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {getBirthTypeBadge(story.birth_type)}
                      {story.was_induced && (
                        <Badge className="bg-purple-100 text-purple-700 font-normal">Inducción</Badge>
                      )}
                      <Badge variant="outline" className="font-normal">{story.birth_year}</Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-[#6B5B5B] line-clamp-3 mb-4">{story.content}</p>
                  
                  <div className={`grid ${story.is_home_birth ? 'grid-cols-1' : 'grid-cols-3'} gap-4 mb-4`}>
                    {!story.is_home_birth && (
                      <>
                        <div className="text-center">
                          <p className="text-xs text-[#8B7B7B] mb-1">Hospital</p>
                          <StarRating value={story.ratings.hospital} readonly size="sm" />
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-[#8B7B7B] mb-1">Ginecología</p>
                          <StarRating value={story.ratings.gynecology} readonly size="sm" />
                        </div>
                      </>
                    )}
                    <div className="text-center">
                      <p className="text-xs text-[#8B7B7B] mb-1">Matrona</p>
                      <StarRating value={story.ratings.midwife} readonly size="sm" />
                    </div>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-sm text-[#8B7B7B]">
                      {new Date(story.created_at).toLocaleDateString('es-ES')}
                    </span>
                    <Button 
                      variant="ghost" 
                      className="text-[#E8A598] hover:text-[#D99285] hover:bg-[#FFF0EB]"
                      onClick={() => navigate(`/stories/${story.id}`)}
                      data-testid={`read-more-${story.id}`}
                    >
                      Leer más
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// New/Edit Story Page
const StoryFormPage = ({ editMode = false }) => {
  const [hospitals, setHospitals] = useState([]);
  const [formData, setFormData] = useState({
    hospital_id: '',
    title: '',
    content: '',
    birth_year: new Date().getFullYear(),
    birth_type: '',
    was_induced: false,
    ratings: { hospital: 3, gynecology: 3, midwife: 3 },
    questions: { felt_respected: '', would_return: '', room_type: '', companion_allowed: '', companion_not_allowed_when: '' },
    is_home_birth: false,
    home_province: '',
    home_birth_team: '',
    instagram_consent: false,
    image_data: ''
  });
  const [loading, setLoading] = useState(false);
  const [fetchingStory, setFetchingStory] = useState(editMode);
  const [addNewCenter, setAddNewCenter] = useState(false);
  const [newCenter, setNewCenter] = useState({
    name: '',
    city: '',
    province: '',
    latitude: 40.4168,
    longitude: -3.7038,
    type: 'hospital'
  });
  const [showMapPicker, setShowMapPicker] = useState(false);
  const navigate = useNavigate();

  const provinces = [
    "A Coruña", "Álava", "Albacete", "Alicante", "Almería", "Asturias", "Ávila", "Badajoz",
    "Barcelona", "Burgos", "Cáceres", "Cádiz", "Cantabria", "Castellón", "Ceuta", "Ciudad Real", "Córdoba",
    "Cuenca", "Girona", "Granada", "Guadalajara", "Guipúzcoa", "Huelva", "Huesca", "Islas Baleares",
    "Jaén", "La Rioja", "Las Palmas", "León", "Lleida", "Lugo", "Madrid", "Málaga", "Melilla", "Murcia",
    "Navarra", "Ourense", "Palencia", "Pontevedra", "Salamanca", "Santa Cruz de Tenerife",
    "Segovia", "Sevilla", "Soria", "Tarragona", "Teruel", "Toledo", "Valencia", "Valladolid",
    "Vizcaya", "Zamora", "Zaragoza"
  ];

  const centerTypes = [
    { value: 'hospital', label: 'Hospital público' },
    { value: 'private_hospital', label: 'Hospital/Clínica privada' },
    { value: 'birth_center', label: 'Centro de nacimiento' },
    { value: 'clinic', label: 'Clínica' }
  ];

  useEffect(() => {
    fetchHospitals();
  }, []);

  const fetchHospitals = async () => {
    try {
      const response = await axios.get(`${API}/hospitals`);
      if (response.data.length === 0) {
        // Seed hospitals if empty
        await axios.post(`${API}/seed/hospitals`);
        const newResponse = await axios.get(`${API}/hospitals`);
        setHospitals(newResponse.data);
      } else {
        setHospitals(response.data);
      }
    } catch (error) {
      toast.error('Error al cargar hospitales');
    } finally {
      setFetchingStory(false);
    }
  };

  const handleImageSelect = (file) => {
    // Validate file size (max 2MB)
    const maxSize = 2 * 1024 * 1024; // 2MB in bytes
    if (file.size > maxSize) {
      toast.error('La imagen es demasiado grande. Tamaño máximo: 2 MB');
      return;
    }

    // Convert to base64
    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData({ ...formData, image_data: reader.result });
    };
    reader.onerror = () => {
      toast.error('Error al cargar la imagen');
    };
    reader.readAsDataURL(file);
  };

  const handleAddNewCenter = async () => {
    if (!newCenter.name || !newCenter.city || !newCenter.province) {
      toast.error('Completa todos los campos del nuevo centro');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/hospitals`, newCenter);
      toast.success('Centro añadido correctamente');
      setHospitals([...hospitals, response.data].sort((a, b) => a.name.localeCompare(b.name)));
      setFormData({ ...formData, hospital_id: response.data.id });
      setAddNewCenter(false);
      setShowMapPicker(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al añadir el centro');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.hospital_id && !formData.is_home_birth) {
      toast.error('Selecciona un hospital o indica que fue parto en domicilio');
      return;
    }
    if (formData.is_home_birth && !formData.home_province) {
      toast.error('Selecciona la provincia donde fue el parto en domicilio');
      return;
    }
    if (!formData.birth_type) {
      toast.error('Selecciona el tipo de parto');
      return;
    }
    
    // Validate ratings (must be at least 1)
    if (!formData.is_home_birth) {
      if (formData.ratings.hospital < 1 || formData.ratings.gynecology < 1 || formData.ratings.midwife < 1) {
        toast.error('Completa todas las valoraciones (mínimo 1 estrella)');
        return;
      }
    } else {
      if (formData.ratings.midwife < 1) {
        toast.error('Completa la valoración de la matrona (mínimo 1 estrella)');
        return;
      }
    }
    
    setLoading(true);
    try {
      const dataToSend = {
        ...formData,
        hospital_id: formData.is_home_birth ? 'home' : formData.hospital_id
      };
      
      await axios.post(`${API}/stories`, dataToSend);
      toast.success('¡Historia publicada con éxito!');
      navigate('/stories');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al publicar la historia');
    } finally {
      setLoading(false);
    }
  };

  const years = Array.from({ length: 30 }, (_, i) => new Date().getFullYear() - i);

  if (fetchingStory) {
    return (
      <div className="min-h-screen bg-[#FFF8F5]">
        <Navigation />
        <div className="flex justify-center py-12">
          <div className="animate-pulse text-[#6B5B5B]">Cargando...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFF8F5]">
      <Navigation />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="border-none shadow-lg">
          <CardHeader>
            <CardTitle className="font-['Playfair_Display'] text-2xl text-[#5D4E4E]">
              {editMode ? 'Editar historia' : 'Comparte tu historia de parto'}
            </CardTitle>
            <CardDescription className="text-[#6B5B5B]">
              Tu experiencia es valiosa y puede ayudar a otras madres. Tu identidad permanecerá anónima.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Home birth toggle */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="is_home_birth"
                  checked={formData.is_home_birth}
                  onChange={(e) => setFormData({ ...formData, is_home_birth: e.target.checked, hospital_id: '' })}
                  className="w-4 h-4 text-[#E8A598] rounded border-[#E8D8D8] focus:ring-[#E8A598]"
                  data-testid="home-birth-checkbox"
                />
                <Label htmlFor="is_home_birth" className="text-[#5D4E4E]">Fue un parto en domicilio</Label>
              </div>

              {formData.is_home_birth ? (
                <div className="space-y-2">
                  <Label className="text-[#5D4E4E]">Provincia del parto en domicilio *</Label>
                  <Select 
                    value={formData.home_province} 
                    onValueChange={(val) => setFormData({ ...formData, home_province: val })}
                  >
                    <SelectTrigger className="border-[#E8D8D8]" data-testid="home-province-select">
                      <SelectValue placeholder="Selecciona una provincia" />
                    </SelectTrigger>
                    <SelectContent>
                      {provinces.map(p => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : addNewCenter ? (
                <div className="space-y-4 p-4 bg-[#FFF0EB] rounded-lg">
                  <div className="flex justify-between items-center">
                    <Label className="text-[#5D4E4E] text-lg">Añadir nuevo centro</Label>
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setAddNewCenter(false)}
                      className="text-[#6B5B5B]"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-[#5D4E4E]">Nombre del centro *</Label>
                    <Input
                      value={newCenter.name}
                      onChange={(e) => setNewCenter({ ...newCenter, name: e.target.value })}
                      placeholder="Ej: Hospital Santa María"
                      className="border-[#E8D8D8] focus:border-[#E8A598] focus:ring-[#E8A598]"
                      data-testid="new-center-name"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-[#5D4E4E]">Provincia *</Label>
                      <Select 
                        value={newCenter.province} 
                        onValueChange={(val) => setNewCenter({ ...newCenter, province: val })}
                      >
                        <SelectTrigger className="border-[#E8D8D8]" data-testid="new-center-province">
                          <SelectValue placeholder="Selecciona" />
                        </SelectTrigger>
                        <SelectContent>
                          {provinces.map(p => (
                            <SelectItem key={p} value={p}>{p}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#5D4E4E]">Ciudad *</Label>
                      <Input
                        value={newCenter.city}
                        onChange={(e) => setNewCenter({ ...newCenter, city: e.target.value })}
                        placeholder="Ej: Madrid"
                        className="border-[#E8D8D8] focus:border-[#E8A598] focus:ring-[#E8A598]"
                        data-testid="new-center-city"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#5D4E4E]">Tipo de centro</Label>
                    <Select 
                      value={newCenter.type} 
                      onValueChange={(val) => setNewCenter({ ...newCenter, type: val })}
                    >
                      <SelectTrigger className="border-[#E8D8D8]" data-testid="new-center-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {centerTypes.map(t => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#5D4E4E]">Ubicación en el mapa</Label>
                    <p className="text-sm text-[#8B7B7B]">Haz clic en el mapa para marcar la ubicación del centro</p>
                    <div className="h-[250px] rounded-lg overflow-hidden border border-[#E8D8D8]">
                      <MapContainer
                        center={[newCenter.latitude, newCenter.longitude]}
                        zoom={6}
                        style={{ height: '100%', width: '100%' }}
                      >
                        <TileLayer
                          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        <MapClickHandler onMapClick={(lat, lng) => setNewCenter({ ...newCenter, latitude: lat, longitude: lng })} />
                        <Marker position={[newCenter.latitude, newCenter.longitude]}>
                          <Popup>{newCenter.name || 'Nuevo centro'}</Popup>
                        </Marker>
                      </MapContainer>
                    </div>
                    <p className="text-xs text-[#8B7B7B]">
                      Coordenadas: {newCenter.latitude.toFixed(4)}, {newCenter.longitude.toFixed(4)}
                    </p>
                  </div>

                  <Button 
                    type="button"
                    onClick={handleAddNewCenter}
                    className="w-full bg-[#E8A598] hover:bg-[#D99285] text-white"
                    data-testid="save-new-center-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Guardar centro y continuar
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <Label className="text-[#5D4E4E]">Hospital / Centro *</Label>
                  <Select 
                    value={formData.hospital_id} 
                    onValueChange={(val) => setFormData({ ...formData, hospital_id: val })}
                  >
                    <SelectTrigger className="border-[#E8D8D8]" data-testid="hospital-select">
                      <SelectValue placeholder="Selecciona un hospital o centro" />
                    </SelectTrigger>
                    <SelectContent>
                      <ScrollArea className="h-[300px]">
                        {hospitals.filter(h => h.type !== 'home').map(h => (
                          <SelectItem key={h.id} value={h.id}>
                            {h.name} ({h.province})
                          </SelectItem>
                        ))}
                      </ScrollArea>
                    </SelectContent>
                  </Select>
                  <Button 
                    type="button"
                    variant="outline"
                    onClick={() => setAddNewCenter(true)}
                    className="w-full border-dashed border-[#E8A598] text-[#E8A598] hover:bg-[#FFF0EB]"
                    data-testid="add-new-center-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Mi centro no aparece en la lista
                  </Button>
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-[#5D4E4E]">Título de tu historia *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Ej: Mi experiencia en el Hospital La Paz"
                  className="border-[#E8D8D8] focus:border-[#E8A598] focus:ring-[#E8A598]"
                  required
                  data-testid="story-title-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#5D4E4E]">Año del parto *</Label>
                  <Select 
                    value={String(formData.birth_year)} 
                    onValueChange={(val) => setFormData({ ...formData, birth_year: parseInt(val) })}
                  >
                    <SelectTrigger className="border-[#E8D8D8]" data-testid="birth-year-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {years.map(y => (
                        <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#5D4E4E]">Tipo de parto *</Label>
                  <Select 
                    value={formData.birth_type} 
                    onValueChange={(val) => setFormData({ ...formData, birth_type: val })}
                  >
                    <SelectTrigger className="border-[#E8D8D8]" data-testid="birth-type-select">
                      <SelectValue placeholder="Selecciona" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="vaginal_normal">Parto vaginal normal</SelectItem>
                      <SelectItem value="vaginal_instrumental">Parto vaginal con ventosa o fórceps</SelectItem>
                      <SelectItem value="cesarean">Parto por cesárea</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Inducido checkbox */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="was_induced"
                  checked={formData.was_induced || false}
                  onChange={(e) => setFormData({ ...formData, was_induced: e.target.checked })}
                  className="w-4 h-4 text-[#E8A598] rounded border-[#E8D8D8] focus:ring-[#E8A598]"
                  data-testid="was-induced-checkbox"
                />
                <Label htmlFor="was_induced" className="text-[#5D4E4E]">Fue un parto inducido</Label>
              </div>

              <div className="space-y-2">
                <Label className="text-[#5D4E4E]">Tu historia *</Label>
                <Textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="Cuenta tu experiencia: cómo te trataron, el proceso, lo que te gustó y lo que mejorarías..."
                  className="border-[#E8D8D8] focus:border-[#E8A598] focus:ring-[#E8A598] min-h-[200px]"
                  required
                  data-testid="story-content-textarea"
                />
              </div>

              {/* Midwife team field for home births */}
              {formData.is_home_birth && (
                <div className="space-y-2">
                  <Label className="text-[#5D4E4E]">Nombre de la matrona o equipo</Label>
                  <Input
                    value={formData.home_birth_team}
                    onChange={(e) => setFormData({ ...formData, home_birth_team: e.target.value })}
                    placeholder="Ej: Matrona María García / Equipo de matronas XYZ"
                    className="border-[#E8D8D8] focus:border-[#E8A598] focus:ring-[#E8A598]"
                    data-testid="home-birth-team-input"
                  />
                </div>
              )}

              {/* Instagram consent checkbox */}
              <div className="flex items-start gap-3 p-4 bg-[#FFF0EB] rounded-lg">
                <input
                  type="checkbox"
                  id="instagram_consent"
                  checked={formData.instagram_consent}
                  onChange={(e) => setFormData({ ...formData, instagram_consent: e.target.checked })}
                  className="w-4 h-4 mt-1 text-[#E8A598] rounded border-[#E8D8D8] focus:ring-[#E8A598]"
                  data-testid="instagram-consent-checkbox"
                />
                <Label htmlFor="instagram_consent" className="text-[#5D4E4E] cursor-pointer">
                  Acepto que se publique mi relato en Instagram (@julia_nacerconamor)
                </Label>
              </div>

              {/* Image upload field */}
              <div className="space-y-2">
                <Label className="text-[#5D4E4E]">Imagen (opcional)</Label>
                <div 
                  className="border-2 border-dashed border-[#E8D8D8] rounded-lg p-6 text-center hover:border-[#E8A598] transition-colors cursor-pointer"
                  onClick={() => document.getElementById('image-upload').click()}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    const file = e.dataTransfer.files[0];
                    if (file && file.type.startsWith('image/')) {
                      handleImageSelect(file);
                    }
                  }}
                >
                  <input
                    id="image-upload"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) handleImageSelect(file);
                    }}
                    data-testid="image-upload-input"
                  />
                  {formData.image_data ? (
                    <div className="space-y-2">
                      <img 
                        src={formData.image_data} 
                        alt="Preview" 
                        className="max-h-40 mx-auto rounded-lg"
                      />
                      <p className="text-sm text-[#6B5B5B]">Click para cambiar la imagen</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="text-[#E8A598] text-4xl">📸</div>
                      <p className="text-[#6B5B5B]">
                        Arrastra una imagen aquí o haz click para seleccionar
                      </p>
                      <p className="text-xs text-[#8B7B7B]">
                        Tamaño máximo: 2 MB
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Ratings */}
              <div className="space-y-4">
                <Label className="text-[#5D4E4E] text-lg">Valoraciones</Label>
                
                <div className="grid gap-4">
                  {!formData.is_home_birth && (
                    <>
                      <div className="flex items-center justify-between">
                        <span className="text-[#6B5B5B]">Instalaciones y servicio del hospital</span>
                        <StarRating 
                          value={formData.ratings.hospital} 
                          onChange={(val) => setFormData({ ...formData, ratings: { ...formData.ratings, hospital: val } })}
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-[#6B5B5B]">Servicio de ginecología</span>
                        <StarRating 
                          value={formData.ratings.gynecology} 
                          onChange={(val) => setFormData({ ...formData, ratings: { ...formData.ratings, gynecology: val } })}
                        />
                      </div>
                    </>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-[#6B5B5B]">Atención de la matrona</span>
                    <StarRating 
                      value={formData.ratings.midwife} 
                      onChange={(val) => setFormData({ ...formData, ratings: { ...formData.ratings, midwife: val } })}
                    />
                  </div>
                </div>
              </div>

              {/* Questions */}
              <div className="space-y-4">
                <Label className="text-[#5D4E4E] text-lg">Preguntas adicionales</Label>
                
                <div className="grid gap-4">
                  <div className="space-y-2">
                    <span className="text-[#6B5B5B]">¿Sentiste que os respetaron a ti y tu bebé?</span>
                    <div className="flex gap-2">
                      {[
                        { value: 'yes', label: 'Sí' },
                        { value: 'more_or_less', label: 'Más o menos' },
                        { value: 'no', label: 'No' }
                      ].map((opt) => (
                        <Button
                          key={opt.value}
                          type="button"
                          variant={formData.questions.felt_respected === opt.value ? 'default' : 'outline'}
                          className={formData.questions.felt_respected === opt.value 
                            ? 'bg-[#E8A598] hover:bg-[#D99285] text-white' 
                            : 'border-[#E8D8D8] text-[#6B5B5B] hover:bg-[#FFF0EB]'}
                          onClick={() => setFormData({ ...formData, questions: { ...formData.questions, felt_respected: opt.value } })}
                          data-testid={`felt-respected-${opt.value}`}
                        >
                          {opt.label}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <span className="text-[#6B5B5B]">¿Volverías a parir en este centro o con este equipo?</span>
                    <div className="flex gap-2">
                      {[
                        { value: 'yes', label: 'Sí' },
                        { value: 'more_or_less', label: 'Es posible' },
                        { value: 'no', label: 'No' }
                      ].map((opt) => (
                        <Button
                          key={opt.value}
                          type="button"
                          variant={formData.questions.would_return === opt.value ? 'default' : 'outline'}
                          className={formData.questions.would_return === opt.value 
                            ? 'bg-[#E8A598] hover:bg-[#D99285] text-white' 
                            : 'border-[#E8D8D8] text-[#6B5B5B] hover:bg-[#FFF0EB]'}
                          onClick={() => setFormData({ ...formData, questions: { ...formData.questions, would_return: opt.value } })}
                          data-testid={`would-return-${opt.value}`}
                        >
                          {opt.label}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <span className="text-[#6B5B5B]">¿La habitación era individual o doble?</span>
                    <div className="flex gap-2">
                      {[
                        { value: 'individual', label: 'Individual' },
                        { value: 'double', label: 'Doble' }
                      ].map((opt) => (
                        <Button
                          key={opt.value}
                          type="button"
                          variant={formData.questions.room_type === opt.value ? 'default' : 'outline'}
                          className={formData.questions.room_type === opt.value 
                            ? 'bg-[#E8A598] hover:bg-[#D99285] text-white' 
                            : 'border-[#E8D8D8] text-[#6B5B5B] hover:bg-[#FFF0EB]'}
                          onClick={() => setFormData({ ...formData, questions: { ...formData.questions, room_type: opt.value } })}
                          data-testid={`room-type-${opt.value}`}
                        >
                          {opt.label}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <span className="text-[#6B5B5B]">¿Dejaron que tu acompañante estuviera siempre contigo?</span>
                    <div className="flex gap-2">
                      {[
                        { value: 'yes', label: 'Sí' },
                        { value: 'no', label: 'No' }
                      ].map((opt) => (
                        <Button
                          key={opt.value}
                          type="button"
                          variant={formData.questions.companion_allowed === opt.value ? 'default' : 'outline'}
                          className={formData.questions.companion_allowed === opt.value 
                            ? 'bg-[#E8A598] hover:bg-[#D99285] text-white' 
                            : 'border-[#E8D8D8] text-[#6B5B5B] hover:bg-[#FFF0EB]'}
                          onClick={() => setFormData({ ...formData, questions: { ...formData.questions, companion_allowed: opt.value, companion_not_allowed_when: opt.value === 'yes' ? '' : formData.questions.companion_not_allowed_when } })}
                          data-testid={`companion-allowed-${opt.value}`}
                        >
                          {opt.label}
                        </Button>
                      ))}
                    </div>
                    {formData.questions.companion_allowed === 'no' && (
                      <div className="mt-2">
                        <Input
                          value={formData.questions.companion_not_allowed_when || ''}
                          onChange={(e) => setFormData({ ...formData, questions: { ...formData.questions, companion_not_allowed_when: e.target.value } })}
                          placeholder="¿En qué momento no dejaron que estuviera contigo?"
                          className="border-[#E8D8D8] focus:border-[#E8A598] focus:ring-[#E8A598]"
                          data-testid="companion-not-allowed-when"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={() => navigate(-1)}
                  className="flex-1 border-[#E8D8D8] text-[#6B5B5B]"
                >
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  className="flex-1 bg-[#E8A598] hover:bg-[#D99285] text-white"
                  disabled={loading}
                  data-testid="submit-story-btn"
                >
                  {loading ? 'Publicando...' : 'Publicar historia'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Story Detail Page
const StoryDetailPage = () => {
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    fetchStory();
  }, []);

  const fetchStory = async () => {
    const id = window.location.pathname.split('/').pop();
    try {
      const response = await axios.get(`${API}/stories/${id}`);
      setStory(response.data);
    } catch (error) {
      toast.error('Historia no encontrada');
      navigate('/stories');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/stories/${story.id}`);
      toast.success('Historia eliminada');
      navigate('/stories');
    } catch (error) {
      toast.error('Error al eliminar la historia');
    }
  };

  const getBirthTypeLabel = (type) => {
    const types = { 
      'vaginal_normal': 'Parto vaginal normal', 
      'vaginal_instrumental': 'Parto con ventosa/fórceps',
      'cesarean': 'Cesárea', 
      'natural': 'Parto Natural', 
      'induced': 'Parto Inducido' 
    };
    return types[type] || type;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFF8F5]">
        <Navigation />
        <div className="flex justify-center py-12">
          <div className="animate-pulse text-[#6B5B5B]">Cargando...</div>
        </div>
      </div>
    );
  }

  if (!story) return null;

  return (
    <div className="min-h-screen bg-[#FFF8F5]">
      <Navigation />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/stories')}
          className="mb-4 text-[#6B5B5B]"
        >
          ← Volver a historias
        </Button>

        <Card className="border-none shadow-lg">
          <CardHeader>
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="font-['Playfair_Display'] text-2xl text-[#5D4E4E] mb-2">
                  {story.title}
                </CardTitle>
                <CardDescription className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  {story.hospital_name} • {story.hospital_province}
                </CardDescription>
              </div>
              {(story.is_owner || user?.is_admin) && (
                <div className="flex gap-2">
                  {story.is_owner && (
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => navigate(`/stories/${story.id}/edit`)}
                      className="text-[#E8A598] hover:text-[#D99285] hover:bg-[#FFF0EB]"
                      data-testid="edit-story-btn"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                  )}
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => setDeleteDialogOpen(true)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    data-testid="delete-story-btn"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
            <div className="flex gap-2 mt-2 flex-wrap">
              <Badge className="bg-[#FFF0EB] text-[#D99285]">{getBirthTypeLabel(story.birth_type)}</Badge>
              {story.was_induced && (
                <Badge className="bg-purple-100 text-purple-700">Inducción</Badge>
              )}
              <Badge variant="outline">{story.birth_year}</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-[#6B5B5B] whitespace-pre-wrap mb-6 leading-relaxed">
              {story.content}
            </p>

            {/* Display uploaded image */}
            {story.image_data && (
              <div className="mb-6">
                <img 
                  src={story.image_data} 
                  alt="Imagen del relato" 
                  className="w-full rounded-lg shadow-md max-h-96 object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            )}

            {/* Display midwife team name for home births */}
            {story.is_home_birth && story.home_birth_team && (
              <div className="bg-[#FFF0EB] rounded-lg p-4 mb-4">
                <h3 className="font-['Playfair_Display'] text-lg text-[#5D4E4E] mb-2">Matrona / Equipo</h3>
                <p className="text-[#6B5B5B]">{story.home_birth_team}</p>
              </div>
            )}

            <div className="bg-[#FFF8F5] rounded-lg p-4">
              <h3 className="font-['Playfair_Display'] text-lg text-[#5D4E4E] mb-4">Valoraciones</h3>
              <div className={`grid ${story.is_home_birth ? 'grid-cols-1 max-w-xs mx-auto' : 'grid-cols-3'} gap-4`}>
                {!story.is_home_birth && (
                  <>
                    <div className="text-center">
                      <p className="text-sm text-[#8B7B7B] mb-2">Hospital</p>
                      <StarRating value={story.ratings.hospital} readonly />
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-[#8B7B7B] mb-2">Ginecología</p>
                      <StarRating value={story.ratings.gynecology} readonly />
                    </div>
                  </>
                )}
                <div className="text-center">
                  <p className="text-sm text-[#8B7B7B] mb-2">Matrona</p>
                  <StarRating value={story.ratings.midwife} readonly />
                </div>
              </div>
            </div>

            {story.questions && (story.questions.felt_respected || story.questions.would_return || story.questions.room_type || story.questions.companion_allowed) && (
              <div className="bg-[#FFF8F5] rounded-lg p-4 mt-4">
                <h3 className="font-['Playfair_Display'] text-lg text-[#5D4E4E] mb-4">Preguntas adicionales</h3>
                <div className="grid gap-3">
                  {story.questions.felt_respected && story.questions.felt_respected !== 'no_answer' && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-[#8B7B7B]">¿Os respetaron a ti y tu bebé?</span>
                      <Badge className={
                        story.questions.felt_respected === 'yes' ? 'bg-green-100 text-green-700' :
                        story.questions.felt_respected === 'more_or_less' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }>
                        {story.questions.felt_respected === 'yes' ? 'Sí' : 
                         story.questions.felt_respected === 'more_or_less' ? 'Más o menos' : 'No'}
                      </Badge>
                    </div>
                  )}
                  {story.questions.would_return && story.questions.would_return !== 'no_answer' && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-[#8B7B7B]">¿Volverías a este centro?</span>
                      <Badge className={
                        story.questions.would_return === 'yes' ? 'bg-green-100 text-green-700' :
                        story.questions.would_return === 'more_or_less' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }>
                        {story.questions.would_return === 'yes' ? 'Sí' : 
                         story.questions.would_return === 'more_or_less' ? 'Es posible' : 'No'}
                      </Badge>
                    </div>
                  )}
                  {story.questions.room_type && story.questions.room_type !== 'no_answer' && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-[#8B7B7B]">Tipo de habitación</span>
                      <Badge className="bg-blue-100 text-blue-700">
                        {story.questions.room_type === 'individual' ? 'Individual' : 'Doble'}
                      </Badge>
                    </div>
                  )}
                  {story.questions.companion_allowed && story.questions.companion_allowed !== 'no_answer' && (
                    <div className="space-y-1">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-[#8B7B7B]">¿Acompañante siempre contigo?</span>
                        <Badge className={story.questions.companion_allowed === 'yes' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                          {story.questions.companion_allowed === 'yes' ? 'Sí' : 'No'}
                        </Badge>
                      </div>
                      {story.questions.companion_allowed === 'no' && story.questions.companion_not_allowed_when && (
                        <p className="text-sm text-[#8B7B7B] italic pl-2 border-l-2 border-[#E8D8D8]">
                          "{story.questions.companion_not_allowed_when}"
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            <p className="text-sm text-[#8B7B7B] mt-4">
              Publicado el {new Date(story.created_at).toLocaleDateString('es-ES', { 
                year: 'numeric', month: 'long', day: 'numeric' 
              })}
            </p>
          </CardContent>
        </Card>

        {/* Call to action for non-authenticated users */}
        {!user && (
          <Card className="border-2 border-[#E8A598] bg-gradient-to-r from-[#FFF8F5] to-[#FFF0EB] mt-6">
            <CardContent className="pt-6">
              <div className="text-center">
                <h3 className="font-['Playfair_Display'] text-xl text-[#5D4E4E] mb-2">
                  ¿Tienes una historia que compartir?
                </h3>
                <p className="text-[#6B5B5B] mb-4">
                  Ayuda a otras madres compartiendo tu experiencia de parto de forma anónima
                </p>
                <div className="flex gap-3 justify-center">
                  <Button 
                    onClick={() => navigate('/register')}
                    className="bg-[#E8A598] hover:bg-[#D99285] text-white"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Compartir mi historia
                  </Button>
                  <Button 
                    onClick={() => navigate('/login')}
                    variant="outline"
                    className="border-[#E8A598] text-[#E8A598] hover:bg-[#FFF0EB]"
                  >
                    Ya tengo cuenta
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Delete Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-[#5D4E4E]">¿Eliminar esta historia?</DialogTitle>
              <DialogDescription>
                Esta acción no se puede deshacer. La historia será eliminada permanentemente.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleDelete}
                className="bg-red-500 hover:bg-red-600 text-white"
                data-testid="confirm-delete-btn"
              >
                Eliminar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

// My Stories Page
const MyStoriesPage = () => {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchMyStories();
  }, []);

  const fetchMyStories = async () => {
    try {
      const response = await axios.get(`${API}/stories/mine`);
      setStories(response.data);
    } catch (error) {
      toast.error('Error al cargar tus historias');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFF8F5]">
      <Navigation />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="font-['Playfair_Display'] text-3xl font-semibold text-[#5D4E4E]">
            Mis Historias
          </h1>
          <Button 
            onClick={() => navigate('/stories/new')}
            className="bg-[#E8A598] hover:bg-[#D99285] text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nueva historia
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-pulse text-[#6B5B5B]">Cargando...</div>
          </div>
        ) : stories.length === 0 ? (
          <Card className="border-none shadow-sm">
            <CardContent className="py-12 text-center">
              <BookOpen className="w-12 h-12 text-[#E8A598] mx-auto mb-4" />
              <p className="text-[#6B5B5B] mb-4">Aún no has compartido ninguna historia</p>
              <Button 
                onClick={() => navigate('/stories/new')}
                className="bg-[#E8A598] hover:bg-[#D99285] text-white"
              >
                Compartir mi primera historia
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6">
            {stories.map((story) => (
              <Card key={story.id} className="border-none shadow-sm hover:shadow-md transition-shadow bg-white">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <CardTitle className="text-[#5D4E4E] font-['Playfair_Display']">
                          {story.title}
                        </CardTitle>
                        {story.instagram_consent && (
                          <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs">
                            📱 Instagram
                          </Badge>
                        )}
                      </div>
                      <CardDescription className="flex items-center gap-2 mt-1">
                        <MapPin className="w-4 h-4" />
                        {story.hospital_name} • {story.hospital_province}
                      </CardDescription>
                    </div>
                    <Badge variant="outline">{story.birth_year}</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-[#6B5B5B] line-clamp-2 mb-4">{story.content}</p>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-[#8B7B7B]">
                      {new Date(story.created_at).toLocaleDateString('es-ES')}
                    </span>
                    <Button 
                      variant="ghost" 
                      className="text-[#E8A598] hover:text-[#D99285]"
                      onClick={() => navigate(`/stories/${story.id}`)}
                    >
                      Ver historia
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Edit Story Page
const EditStoryPage = () => {
  const [story, setStory] = useState(null);
  const [hospitals, setHospitals] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    birth_year: new Date().getFullYear(),
    birth_type: '',
    was_induced: false,
    ratings: { hospital: 3, gynecology: 3, midwife: 3 },
    questions: { felt_respected: '', would_return: '', room_type: '', companion_allowed: '', companion_not_allowed_when: '' },
    home_birth_team: '',
    instagram_consent: false,
    image_data: ''
  });
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchStoryAndHospitals();
  }, []);

  const fetchStoryAndHospitals = async () => {
    const id = window.location.pathname.split('/')[2];
    try {
      const [storyRes, hospitalsRes] = await Promise.all([
        axios.get(`${API}/stories/${id}`),
        axios.get(`${API}/hospitals`)
      ]);
      
      const s = storyRes.data;
      if (!s.is_owner) {
        toast.error('No tienes permiso para editar esta historia');
        navigate('/stories');
        return;
      }
      
      setStory(s);
      setHospitals(hospitalsRes.data);
      setFormData({
        title: s.title,
        content: s.content,
        birth_year: s.birth_year,
        birth_type: s.birth_type,
        was_induced: s.was_induced || false,
        ratings: s.ratings,
        questions: s.questions || { felt_respected: '', would_return: '', room_type: '', companion_allowed: '', companion_not_allowed_when: '' },
        home_birth_team: s.home_birth_team || '',
        instagram_consent: s.instagram_consent || false,
        image_data: s.image_data || ''
      });
    } catch (error) {
      toast.error('Error al cargar la historia');
      navigate('/stories');
    } finally {
      setFetching(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate ratings (must be at least 1)
    if (!story?.is_home_birth) {
      if (formData.ratings.hospital < 1 || formData.ratings.gynecology < 1 || formData.ratings.midwife < 1) {
        toast.error('Completa todas las valoraciones (mínimo 1 estrella)');
        return;
      }
    } else {
      if (formData.ratings.midwife < 1) {
        toast.error('Completa la valoración de la matrona (mínimo 1 estrella)');
        return;
      }
    }
    
    setLoading(true);
    try {
      await axios.put(`${API}/stories/${story.id}`, formData);
      toast.success('Historia actualizada correctamente');
      navigate(`/stories/${story.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar la historia');
    } finally {
      setLoading(false);
    }
  };

  const handleImageSelectEdit = (file) => {
    // Validate file size (max 2MB)
    const maxSize = 2 * 1024 * 1024; // 2MB in bytes
    if (file.size > maxSize) {
      toast.error('La imagen es demasiado grande. Tamaño máximo: 2 MB');
      return;
    }

    // Convert to base64
    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData({ ...formData, image_data: reader.result });
    };
    reader.onerror = () => {
      toast.error('Error al cargar la imagen');
    };
    reader.readAsDataURL(file);
  };

  const years = Array.from({ length: 30 }, (_, i) => new Date().getFullYear() - i);

  if (fetching) {
    return (
      <div className="min-h-screen bg-[#FFF8F5]">
        <Navigation />
        <div className="flex justify-center py-12">
          <div className="animate-pulse text-[#6B5B5B]">Cargando...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFF8F5]">
      <Navigation />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="border-none shadow-lg">
          <CardHeader>
            <CardTitle className="font-['Playfair_Display'] text-2xl text-[#5D4E4E]">
              Editar historia
            </CardTitle>
            <CardDescription className="text-[#6B5B5B]">
              Modifica los datos de tu historia de parto
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label className="text-[#5D4E4E]">Hospital / Centro</Label>
                <p className="text-sm text-[#8B7B7B] bg-[#FFF8F5] p-2 rounded">
                  {story?.hospital_name} ({story?.hospital_province})
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-[#5D4E4E]">Título de tu historia *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="border-[#E8D8D8] focus:border-[#E8A598] focus:ring-[#E8A598]"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#5D4E4E]">Año del parto *</Label>
                  <Select 
                    value={String(formData.birth_year)} 
                    onValueChange={(val) => setFormData({ ...formData, birth_year: parseInt(val) })}
                  >
                    <SelectTrigger className="border-[#E8D8D8]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {years.map(y => (
                        <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#5D4E4E]">Tipo de parto *</Label>
                  <Select 
                    value={formData.birth_type} 
                    onValueChange={(val) => setFormData({ ...formData, birth_type: val })}
                  >
                    <SelectTrigger className="border-[#E8D8D8]">
                      <SelectValue placeholder="Selecciona" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="vaginal_normal">Parto vaginal normal</SelectItem>
                      <SelectItem value="vaginal_instrumental">Parto vaginal con ventosa o fórceps</SelectItem>
                      <SelectItem value="cesarean">Parto por cesárea</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="was_induced_edit"
                  checked={formData.was_induced}
                  onChange={(e) => setFormData({ ...formData, was_induced: e.target.checked })}
                  className="w-4 h-4 text-[#E8A598] rounded border-[#E8D8D8] focus:ring-[#E8A598]"
                />
                <Label htmlFor="was_induced_edit" className="text-[#5D4E4E]">Fue un parto inducido</Label>
              </div>

              <div className="space-y-2">
                <Label className="text-[#5D4E4E]">Tu historia *</Label>
                <Textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  className="border-[#E8D8D8] focus:border-[#E8A598] focus:ring-[#E8A598] min-h-[200px]"
                  required
                />
              </div>

              {/* Midwife team field for home births */}
              {story?.is_home_birth && (
                <div className="space-y-2">
                  <Label className="text-[#5D4E4E]">Nombre de la matrona o equipo</Label>
                  <Input
                    value={formData.home_birth_team}
                    onChange={(e) => setFormData({ ...formData, home_birth_team: e.target.value })}
                    placeholder="Ej: Matrona María García / Equipo de matronas XYZ"
                    className="border-[#E8D8D8] focus:border-[#E8A598] focus:ring-[#E8A598]"
                  />
                </div>
              )}

              {/* Instagram consent checkbox */}
              <div className="flex items-start gap-3 p-4 bg-[#FFF0EB] rounded-lg">
                <input
                  type="checkbox"
                  id="instagram_consent_edit"
                  checked={formData.instagram_consent}
                  onChange={(e) => setFormData({ ...formData, instagram_consent: e.target.checked })}
                  className="w-4 h-4 mt-1 text-[#E8A598] rounded border-[#E8D8D8] focus:ring-[#E8A598]"
                />
                <Label htmlFor="instagram_consent_edit" className="text-[#5D4E4E] cursor-pointer">
                  Acepto que se publique mi relato en Instagram (@julia_nacerconamor)
                </Label>
              </div>

              {/* Image upload field */}
              <div className="space-y-2">
                <Label className="text-[#5D4E4E]">Imagen (opcional)</Label>
                <div 
                  className="border-2 border-dashed border-[#E8D8D8] rounded-lg p-6 text-center hover:border-[#E8A598] transition-colors cursor-pointer"
                  onClick={() => document.getElementById('image-upload-edit').click()}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    const file = e.dataTransfer.files[0];
                    if (file && file.type.startsWith('image/')) {
                      handleImageSelectEdit(file);
                    }
                  }}
                >
                  <input
                    id="image-upload-edit"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) handleImageSelectEdit(file);
                    }}
                  />
                  {formData.image_data ? (
                    <div className="space-y-2">
                      <img 
                        src={formData.image_data} 
                        alt="Preview" 
                        className="max-h-40 mx-auto rounded-lg"
                      />
                      <p className="text-sm text-[#6B5B5B]">Click para cambiar la imagen</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="text-[#E8A598] text-4xl">📸</div>
                      <p className="text-[#6B5B5B]">
                        Arrastra una imagen aquí o haz click para seleccionar
                      </p>
                      <p className="text-xs text-[#8B7B7B]">
                        Tamaño máximo: 2 MB
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Ratings */}
              <div className="space-y-4">
                <Label className="text-[#5D4E4E] text-lg">Valoraciones</Label>
                <div className="grid gap-4">
                  {!story?.is_home_birth && (
                    <>
                      <div className="flex items-center justify-between">
                        <span className="text-[#6B5B5B]">Instalaciones y servicio del hospital</span>
                        <StarRating 
                          value={formData.ratings.hospital} 
                          onChange={(val) => setFormData({ ...formData, ratings: { ...formData.ratings, hospital: val } })}
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-[#6B5B5B]">Servicio de ginecología</span>
                        <StarRating 
                          value={formData.ratings.gynecology} 
                          onChange={(val) => setFormData({ ...formData, ratings: { ...formData.ratings, gynecology: val } })}
                        />
                      </div>
                    </>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-[#6B5B5B]">Atención de la matrona</span>
                    <StarRating 
                      value={formData.ratings.midwife} 
                      onChange={(val) => setFormData({ ...formData, ratings: { ...formData.ratings, midwife: val } })}
                    />
                  </div>
                </div>
              </div>

              {/* Questions */}
              <div className="space-y-4">
                <Label className="text-[#5D4E4E] text-lg">Preguntas adicionales</Label>
                <div className="grid gap-4">
                  <div className="space-y-2">
                    <span className="text-[#6B5B5B]">¿Sentiste que os respetaron a ti y tu bebé?</span>
                    <div className="flex gap-2">
                      {[{ value: 'yes', label: 'Sí' }, { value: 'more_or_less', label: 'Más o menos' }, { value: 'no', label: 'No' }].map((opt) => (
                        <Button
                          key={opt.value}
                          type="button"
                          variant={formData.questions.felt_respected === opt.value ? 'default' : 'outline'}
                          className={formData.questions.felt_respected === opt.value 
                            ? 'bg-[#E8A598] hover:bg-[#D99285] text-white' 
                            : 'border-[#E8D8D8] text-[#6B5B5B] hover:bg-[#FFF0EB]'}
                          onClick={() => setFormData({ ...formData, questions: { ...formData.questions, felt_respected: opt.value } })}
                        >
                          {opt.label}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <span className="text-[#6B5B5B]">¿Volverías a parir en este centro o con este equipo?</span>
                    <div className="flex gap-2">
                      {[{ value: 'yes', label: 'Sí' }, { value: 'more_or_less', label: 'Es posible' }, { value: 'no', label: 'No' }].map((opt) => (
                        <Button
                          key={opt.value}
                          type="button"
                          variant={formData.questions.would_return === opt.value ? 'default' : 'outline'}
                          className={formData.questions.would_return === opt.value 
                            ? 'bg-[#E8A598] hover:bg-[#D99285] text-white' 
                            : 'border-[#E8D8D8] text-[#6B5B5B] hover:bg-[#FFF0EB]'}
                          onClick={() => setFormData({ ...formData, questions: { ...formData.questions, would_return: opt.value } })}
                        >
                          {opt.label}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <span className="text-[#6B5B5B]">¿La habitación era individual o doble?</span>
                    <div className="flex gap-2">
                      {[{ value: 'individual', label: 'Individual' }, { value: 'double', label: 'Doble' }].map((opt) => (
                        <Button
                          key={opt.value}
                          type="button"
                          variant={formData.questions.room_type === opt.value ? 'default' : 'outline'}
                          className={formData.questions.room_type === opt.value 
                            ? 'bg-[#E8A598] hover:bg-[#D99285] text-white' 
                            : 'border-[#E8D8D8] text-[#6B5B5B] hover:bg-[#FFF0EB]'}
                          onClick={() => setFormData({ ...formData, questions: { ...formData.questions, room_type: opt.value } })}
                        >
                          {opt.label}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <span className="text-[#6B5B5B]">¿Dejaron que tu acompañante estuviera siempre contigo?</span>
                    <div className="flex gap-2">
                      {[{ value: 'yes', label: 'Sí' }, { value: 'no', label: 'No' }].map((opt) => (
                        <Button
                          key={opt.value}
                          type="button"
                          variant={formData.questions.companion_allowed === opt.value ? 'default' : 'outline'}
                          className={formData.questions.companion_allowed === opt.value 
                            ? 'bg-[#E8A598] hover:bg-[#D99285] text-white' 
                            : 'border-[#E8D8D8] text-[#6B5B5B] hover:bg-[#FFF0EB]'}
                          onClick={() => setFormData({ ...formData, questions: { ...formData.questions, companion_allowed: opt.value, companion_not_allowed_when: opt.value === 'yes' ? '' : formData.questions.companion_not_allowed_when } })}
                        >
                          {opt.label}
                        </Button>
                      ))}
                    </div>
                    {formData.questions.companion_allowed === 'no' && (
                      <Input
                        value={formData.questions.companion_not_allowed_when || ''}
                        onChange={(e) => setFormData({ ...formData, questions: { ...formData.questions, companion_not_allowed_when: e.target.value } })}
                        placeholder="¿En qué momento no dejaron que estuviera contigo?"
                        className="border-[#E8D8D8] focus:border-[#E8A598] focus:ring-[#E8A598] mt-2"
                      />
                    )}
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={() => navigate(`/stories/${story.id}`)}
                  className="flex-1 border-[#E8D8D8] text-[#6B5B5B]"
                >
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  className="flex-1 bg-[#E8A598] hover:bg-[#D99285] text-white"
                  disabled={loading}
                >
                  {loading ? 'Guardando...' : 'Guardar cambios'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Map Page

// Instagram Manager Page (Admin only)
const InstagramManagerPage = () => {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStory, setSelectedStory] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/stories');
      return;
    }
    fetchStoriesWithConsent();
  }, [user, navigate]);

  const fetchStoriesWithConsent = async () => {
    try {
      const response = await axios.get(`${API}/stories`);
      const storiesWithConsent = response.data.filter(s => s.instagram_consent);
      setStories(storiesWithConsent);
    } catch (error) {
      toast.error('Error al cargar historias');
    } finally {
      setLoading(false);
    }
  };

  const generateInstagramImage = async (story) => {
    setSelectedStory(story);
  };

  const splitTextIntoChunks = (text, maxCharsPerSlide) => {
    const words = text.split(' ');
    const chunks = [];
    let currentChunk = '';
    
    words.forEach(word => {
      if ((currentChunk + word).length < maxCharsPerSlide) {
        currentChunk += word + ' ';
      } else {
        if (currentChunk) chunks.push(currentChunk.trim());
        currentChunk = word + ' ';
      }
    });
    if (currentChunk) chunks.push(currentChunk.trim());
    
    return chunks;
  };

  const generateSlideImage = async (content, slideNumber, totalSlides, story) => {
    const canvas = document.createElement('canvas');
    canvas.width = 1080;
    canvas.height = 1080;
    const ctx = canvas.getContext('2d');

    // Background gradient
    const gradient = ctx.createLinearGradient(0, 0, 0, 1080);
    gradient.addColorStop(0, '#FFF8F5');
    gradient.addColorStop(1, '#FFE8E0');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 1080, 1080);

    // Header - Brand
    ctx.fillStyle = '#5D4E4E';
    ctx.textAlign = 'center';
    ctx.font = 'bold 40px Playfair Display, serif';
    ctx.fillText('Mi Historia de Parto', 540, 100);

    // Title (only on first slide)
    if (slideNumber === 1) {
      ctx.font = 'bold 56px Playfair Display, serif';
      ctx.fillStyle = '#E8A598';
      const titleLines = story.title.match(/.{1,30}/g) || [story.title];
      titleLines.forEach((line, i) => {
        ctx.fillText(line, 540, 220 + (i * 65));
      });
    }

    // Main content
    ctx.fillStyle = '#5D4E4E';
    ctx.textAlign = 'left';
    ctx.font = '38px Arial, sans-serif';
    
    const words = content.split(' ');
    let line = '';
    let y = slideNumber === 1 ? 350 : 200;
    const lineHeight = 55;
    const maxWidth = 900;
    
    words.forEach(word => {
      const testLine = line + word + ' ';
      const metrics = ctx.measureText(testLine);
      
      if (metrics.width > maxWidth && line !== '') {
        ctx.fillText(line, 90, y);
        line = word + ' ';
        y += lineHeight;
      } else {
        line = testLine;
      }
    });
    ctx.fillText(line, 90, y);

    // Footer
    ctx.textAlign = 'center';
    ctx.fillStyle = '#8B7B7B';
    ctx.font = '26px Arial';
    const location = story.is_home_birth ? `Parto en casa - ${story.hospital_province}` : story.hospital_name;
    ctx.fillText(location, 540, 970);

    ctx.fillStyle = '#E8A598';
    ctx.font = 'bold 30px Arial';
    ctx.fillText('@julia_nacerconamor', 540, 1020);

    // Slide counter
    ctx.fillStyle = '#C9C9C9';
    ctx.font = '24px Arial';
    ctx.fillText(`${slideNumber}/${totalSlides}`, 540, 1060);

    return canvas.toDataURL();
  };

  const downloadImage = async (story) => {
    toast.info('Generando imágenes...');
    
    try {
      if (story.image_data) {
        // If has image, download single image
        const canvas = document.createElement('canvas');
        canvas.width = 1080;
        canvas.height = 1080;
        const ctx = canvas.getContext('2d');

        const img = new Image();
        img.crossOrigin = 'anonymous';
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = story.image_data;
        });
        ctx.drawImage(img, 0, 0, 1080, 1080);
        
        // Add semi-transparent overlay for footer
        ctx.fillStyle = 'rgba(255, 248, 245, 0.85)';
        ctx.fillRect(0, 900, 1080, 180);

        // Footer
        ctx.fillStyle = '#8B7B7B';
        ctx.textAlign = 'center';
        ctx.font = '28px Arial';
        const location = story.is_home_birth ? `Parto en casa - ${story.hospital_province}` : story.hospital_name;
        ctx.fillText(location, 540, 1000);

        ctx.fillStyle = '#E8A598';
        ctx.font = 'bold 32px Arial';
        ctx.fillText('@julia_nacerconamor', 540, 1050);

        const link = document.createElement('a');
        link.download = `historia-${story.id}.png`;
        link.href = canvas.toDataURL();
        link.click();
        
        toast.success('Imagen descargada');
      } else {
        // Generate carousel of images with all text
        const maxCharsPerSlide = 600; // Adjust based on font size
        const chunks = splitTextIntoChunks(story.content, maxCharsPerSlide);
        
        toast.info(`Generando ${chunks.length} imágenes...`);
        
        // Generate all slides
        for (let i = 0; i < chunks.length; i++) {
          const imageData = await generateSlideImage(chunks[i], i + 1, chunks.length, story);
          
          const link = document.createElement('a');
          link.download = `historia-${story.id}-${i + 1}-de-${chunks.length}.png`;
          link.href = imageData;
          link.click();
          
          // Small delay between downloads
          await new Promise(resolve => setTimeout(resolve, 500));
        }
        
        toast.success(`${chunks.length} imágenes descargadas para el carrusel`);
      }
    } catch (error) {
      console.error('Error generating images:', error);
      toast.error('Error al generar imágenes');
    }
  };

  const copyCaption = async (story) => {
    const birthType = {
      'vaginal_normal': 'parto vaginal',
      'vaginal_assisted': 'parto vaginal asistido',
      'cesarean': 'cesárea'
    }[story.birth_type] || story.birth_type;

    const location = story.is_home_birth 
      ? `Parto en casa - ${story.hospital_province}`
      : `${story.hospital_name}, ${story.hospital_province}`;

    let caption = `💕 ${story.title}\n\n`;
    caption += `${story.content}\n\n`;
    caption += `📍 ${location}\n`;
    caption += `👶 ${birthType}\n`;
    caption += `📅 Año ${story.birth_year}\n\n`;
    caption += `#partorespetado #nacimiento #maternidad #parto #embarazo #nacerconamor #historiadeparto`;
    
    try {
      await navigator.clipboard.writeText(caption);
      toast.success('Caption copiado al portapapeles');
    } catch (err) {
      toast.error('Error al copiar');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFF8F5]">
        <Navigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">Cargando...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFF8F5]">
      <Navigation />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="font-['Playfair_Display'] text-3xl font-semibold text-[#5D4E4E] mb-2">
            📱 Gestión de Instagram
          </h1>
          <p className="text-[#8B7B7B]">
            Historias con consentimiento para publicar en @julia_nacerconamor
          </p>
        </div>

        {stories.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-[#6B5B5B]">No hay historias con consentimiento de Instagram aún</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6">
            {stories.map((story) => (
              <Card key={story.id} className="border-2 border-[#E8A598]">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-[#5D4E4E] font-['Playfair_Display']">
                        {story.title}
                      </CardTitle>
                      <CardDescription>
                        {story.hospital_name} • {story.hospital_province} • {story.birth_year}
                      </CardDescription>
                    </div>
                    <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white">
                      📱 Instagram
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-[#6B5B5B] mb-4 line-clamp-3">{story.content}</p>
                  
                  <div className="flex gap-3">
                    <Button
                      onClick={() => downloadImage(story)}
                      className="bg-[#E8A598] hover:bg-[#D99285] text-white"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Descargar Imagen
                    </Button>
                    <Button
                      onClick={() => copyCaption(story)}
                      variant="outline"
                      className="border-[#E8A598] text-[#E8A598] hover:bg-[#FFF0EB]"
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      Copiar Caption
                    </Button>
                    <Button
                      onClick={() => navigate(`/stories/${story.id}`)}
                      variant="ghost"
                      className="text-[#6B5B5B]"
                    >
                      Ver Historia
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};


const MapPage = () => {
  const [stats, setStats] = useState([]);
  const [homeBirthStats, setHomeBirthStats] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedHospital, setSelectedHospital] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, homeBirthsRes, hospitalsRes] = await Promise.all([
        axios.get(`${API}/stats/hospitals`),
        axios.get(`${API}/stats/home-births`),
        axios.get(`${API}/hospitals`)
      ]);
      setStats(statsRes.data);
      setHomeBirthStats(homeBirthsRes.data);
      setHospitals(hospitalsRes.data);
    } catch (error) {
      toast.error('Error al cargar datos del mapa');
    } finally {
      setLoading(false);
    }
  };

  const getMarkerColor = (avgTotal) => {
    if (avgTotal >= 4) return '#22c55e'; // green
    if (avgTotal >= 3) return '#eab308'; // yellow
    return '#ef4444'; // red
  };

  const createCustomIcon = (color, count) => {
    return L.divIcon({
      className: 'custom-marker',
      html: `<div style="background-color: ${color}; width: 30px; height: 30px; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-weight: bold; font-size: 12px; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);">${count}</div>`,
      iconSize: [30, 30],
      iconAnchor: [15, 15]
    });
  };

  return (
    <div className="min-h-screen bg-[#FFF8F5]">
      <Navigation />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h1 className="font-['Playfair_Display'] text-3xl font-semibold text-[#5D4E4E]">
              Mapa de Hospitales
            </h1>
            {!user && (
              <p className="text-sm text-[#8B7B7B] mt-1">
                ✨ Regístrate para compartir tu historia
              </p>
            )}
          </div>
          <Button 
            onClick={() => user ? navigate('/stories/new') : navigate('/register')}
            className="bg-[#E8A598] hover:bg-[#D99285] text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Compartir mi historia
          </Button>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <Card className="border-none shadow-lg overflow-hidden">
              <div className="h-[500px]">
                {!loading && (
                  <MapContainer
                    center={[40.4168, -3.7038]}
                    zoom={6}
                    style={{ height: '100%', width: '100%' }}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    {/* Hospital markers */}
                    {stats.map((stat) => (
                      <Marker
                        key={stat.hospital_id}
                        position={[stat.latitude, stat.longitude]}
                        icon={createCustomIcon(getMarkerColor(stat.avg_total), stat.story_count)}
                        eventHandlers={{
                          click: () => setSelectedHospital(stat)
                        }}
                      >
                        <Popup>
                          <div className="p-2">
                            <h3 className="font-semibold text-[#5D4E4E]">{stat.hospital_name}</h3>
                            <p className="text-sm text-[#6B5B5B]">{stat.province}</p>
                            <p className="text-sm mt-2">{stat.story_count} historias</p>
                            <div className="mt-2">
                              <p className="text-xs">Hospital: {'★'.repeat(Math.round(stat.avg_hospital))}</p>
                              <p className="text-xs">Ginecología: {'★'.repeat(Math.round(stat.avg_gynecology))}</p>
                              <p className="text-xs">Matrona: {'★'.repeat(Math.round(stat.avg_midwife))}</p>
                            </div>
                            <Button
                              size="sm"
                              className="mt-2 bg-[#E8A598] hover:bg-[#D99285] text-white text-xs"
                              onClick={() => navigate(`/stories?hospital=${stat.hospital_id}`)}
                            >
                              Ver historias
                            </Button>
                          </div>
                        </Popup>
                      </Marker>
                    ))}
                    {/* Home birth markers */}
                    {homeBirthStats.map((stat) => (
                      <Marker
                        key={stat.hospital_id}
                        position={[stat.latitude, stat.longitude]}
                        icon={createCustomIcon(getMarkerColor(stat.avg_total), stat.story_count)}
                        eventHandlers={{
                          click: () => setSelectedHospital(stat)
                        }}
                      >
                        <Popup>
                          <div className="p-2">
                            <h3 className="font-semibold text-[#5D4E4E]">Parto en domicilio</h3>
                            <p className="text-sm text-[#6B5B5B]">{stat.province}</p>
                            <p className="text-sm mt-2">{stat.story_count} historias</p>
                            <div className="mt-2">
                              <p className="text-xs">Valoración: {'★'.repeat(Math.round(stat.avg_midwife))}</p>
                            </div>
                          </div>
                        </Popup>
                      </Marker>
                    ))}
                  </MapContainer>
                )}
              </div>
            </Card>

            {/* Legend */}
            <div className="flex flex-wrap gap-4 mt-4 justify-center">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-green-500"></div>
                <span className="text-sm text-[#6B5B5B]">4-5 estrellas</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-yellow-500"></div>
                <span className="text-sm text-[#6B5B5B]">3-4 estrellas</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-red-500"></div>
                <span className="text-sm text-[#6B5B5B]">1-3 estrellas</span>
              </div>
            </div>
          </div>

          <div>
            <Card className="border-none shadow-lg">
              <CardHeader>
                <CardTitle className="font-['Playfair_Display'] text-lg text-[#5D4E4E]">
                  Centros con historias
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  <div className="space-y-3">
                    {stats.length === 0 && homeBirthStats.length === 0 ? (
                      <p className="text-[#6B5B5B] text-center py-4">
                        Aún no hay historias. ¡Sé la primera en compartir!
                      </p>
                    ) : (
                      <>
                        {stats.sort((a, b) => b.avg_total - a.avg_total).map((stat) => (
                          <div 
                            key={stat.hospital_id}
                            className="p-3 bg-[#FFF8F5] rounded-lg cursor-pointer hover:bg-[#FFF0EB] transition-colors"
                            onClick={() => navigate(`/stories?hospital=${stat.hospital_id}`)}
                          >
                            <h4 className="font-medium text-[#5D4E4E] text-sm">{stat.hospital_name}</h4>
                            <p className="text-xs text-[#8B7B7B]">{stat.province}</p>
                            <div className="flex items-center justify-between mt-2">
                              <StarRating value={Math.round(stat.avg_total)} readonly size="sm" />
                              <span className="text-xs text-[#8B7B7B]">{stat.story_count} historias</span>
                            </div>
                          </div>
                        ))}
                        {homeBirthStats.map((stat) => (
                          <div 
                            key={stat.hospital_id}
                            className="p-3 bg-[#FFF8F5] rounded-lg cursor-pointer hover:bg-[#FFF0EB] transition-colors"
                          >
                            <h4 className="font-medium text-[#5D4E4E] text-sm">Parto en domicilio</h4>
                            <p className="text-xs text-[#8B7B7B]">{stat.province}</p>
                            <div className="flex items-center justify-between mt-2">
                              <StarRating value={Math.round(stat.avg_total)} readonly size="sm" />
                              <span className="text-xs text-[#8B7B7B]">{stat.story_count} historias</span>
                            </div>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <Toaster position="top-center" richColors />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/stories" element={<StoriesPage />} />
          <Route path="/stories/new" element={
            <ProtectedRoute><StoryFormPage /></ProtectedRoute>
          } />
          <Route path="/stories/:id/edit" element={
            <ProtectedRoute><EditStoryPage /></ProtectedRoute>
          } />
          <Route path="/stories/:id" element={<StoryDetailPage />} />
          <Route path="/my-stories" element={
            <ProtectedRoute><MyStoriesPage /></ProtectedRoute>
          } />
          <Route path="/instagram-manager" element={
            <ProtectedRoute><InstagramManagerPage /></ProtectedRoute>
          } />
          <Route path="/map" element={<MapPage />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
